import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Package,
  CheckCircle2,
  Star,
  Users,
  HardDrive,
  Loader2,
  CreditCard,
  ShieldCheck,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { PermissionGate } from '@/components/PermissionGate';
import { Permission } from '@/common/enums/permissions.enum';
import {
  subscriptionsTenantService,
  BillingSummary,
  Plan,
} from '@/features/branding/services/subscriptions.service';

export const Route = createFileRoute('/_authenticated/admin/plans')({
  component: PlansRoute,
});

function PlansRoute() {
  return (
    <PermissionGate permission={Permission.ADMIN_TENANT}>
      <PlansPage />
    </PermissionGate>
  );
}

function formatStorage(gb: number) {
  if (gb >= 1024 && gb % 1024 === 0) {
    return `${gb / 1024} TB`;
  }
  return `${gb} GB`;
}

function formatBrl(value: number) {
  return value.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

function PlansPage() {
  const queryClient = useQueryClient();
  const [checkoutPlan, setCheckoutPlan] = useState<Plan | null>(null);

  const { data: plans = [], isLoading: plansLoading } = useQuery<Plan[]>({
    queryKey: ['subscription', 'plans-catalog'],
    queryFn: () => subscriptionsTenantService.listPlans(),
  });

  const { data: billing, isLoading: billingLoading } = useQuery<BillingSummary>({
    queryKey: ['subscription', 'my-billing'],
    queryFn: () => subscriptionsTenantService.getMyBilling(),
  });

  const activePlans = plans.filter((p) => p.isActive).sort((a, b) => a.sortOrder - b.sortOrder);
  const currentPlanLabel = billing?.plan?.label;

  if (plansLoading || billingLoading) {
    return (
      <div className="p-6 max-w-5xl mx-auto space-y-4 animate-pulse">
        <div className="h-8 w-48 bg-muted rounded" />
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="h-80 bg-muted rounded-xl" />
          <div className="h-80 bg-muted rounded-xl" />
          <div className="h-80 bg-muted rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-8">
      {/* Cabeçalho */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Package className="h-6 w-6" />
          Planos
        </h1>
        <p className="text-muted-foreground mt-1">
          Escolha o plano ideal para o seu negócio. Você pode trocar de plano a qualquer momento.
        </p>
      </div>

      {/* Plano atual (resumo rápido) */}
      {billing?.plan && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-muted/40 p-4">
          <div className="flex items-center gap-2 text-sm">
            <CheckCircle2 className="h-4 w-4 text-green-500" />
            <span className="text-muted-foreground">Plano atual:</span>
            <span className="font-semibold">{currentPlanLabel}</span>
            <span className="text-muted-foreground">
              ({billing.userSlots} usuários · {formatStorage(billing.storageGb)})
            </span>
          </div>
          <div className="text-sm">
            <span className="text-muted-foreground">Total mensal: </span>
            <span className="font-semibold">
              R$ {formatBrl(billing.breakdown.total)}
            </span>
          </div>
        </div>
      )}

      {/* Cards de planos */}
      <div className="grid gap-5 sm:grid-cols-3 items-stretch">
        {activePlans.map((plan) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            isCurrent={plan.label === currentPlanLabel}
            onSelect={() => setCheckoutPlan(plan)}
          />
        ))}
      </div>

      {/* Aviso de pagamento simulado */}
      <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/40 p-4 text-sm text-blue-800 dark:text-blue-300">
        <ShieldCheck className="h-4 w-4 mt-0.5 flex-shrink-0" />
        <p>
          Ambiente de testes: a contratação/troca de plano é processada com pagamento
          simulado (sem cobrança real) e ativada imediatamente.
        </p>
      </div>

      {checkoutPlan && (
        <CheckoutDialog
          plan={checkoutPlan}
          billing={billing}
          onClose={() => setCheckoutPlan(null)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['subscription'] });
            setCheckoutPlan(null);
          }}
        />
      )}
    </div>
  );
}

// ─── Card de plano ────────────────────────────────────────────────────────────

function PlanCard({
  plan,
  isCurrent,
  onSelect,
}: {
  plan: Plan;
  isCurrent: boolean;
  onSelect: () => void;
}) {
  const featured = !!plan.isFeatured;

  return (
    <Card
      className={`flex flex-col relative ${
        featured
          ? 'border-primary shadow-lg shadow-primary/10 scale-[1.02]'
          : 'border-border'
      } ${isCurrent ? 'ring-2 ring-green-500/50' : ''}`}
    >
      {featured && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <Badge className="bg-primary text-primary-foreground gap-1 shadow">
            <Star className="h-3 w-3 fill-current" />
            Mais popular
          </Badge>
        </div>
      )}

      <CardHeader className="text-center pb-2 pt-6">
        <CardTitle className="text-xl">{plan.label}</CardTitle>
        <CardDescription>
          <span className="text-3xl font-bold text-foreground">
            R$ {formatBrl(Number(plan.basePriceBrl))}
          </span>
          <span className="text-sm text-muted-foreground">/mês</span>
        </CardDescription>
      </CardHeader>

      <CardContent className="flex-1 space-y-3">
        <Separator />
        <div className="space-y-2.5 text-sm">
          <div className="flex items-center gap-2">
            <HardDrive className="h-4 w-4 text-primary flex-shrink-0" />
            <span>
              <strong>{formatStorage(plan.storageGbIncluded)}</strong> de armazenamento
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-primary flex-shrink-0" />
            <span>
              <strong>{plan.includedUsers} usuários</strong> incluídos
            </span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
            <span>
              + R$ {formatBrl(Number(plan.pricePerExtraUserBrl))}/mês por usuário extra
            </span>
          </div>
        </div>
      </CardContent>

      <CardFooter>
        {isCurrent ? (
          <Button className="w-full" variant="outline" disabled>
            <CheckCircle2 className="h-4 w-4 mr-2 text-green-500" />
            Plano atual
          </Button>
        ) : (
          <Button className="w-full" variant={featured ? 'default' : 'outline'} onClick={onSelect}>
            Contratar este plano
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}

// ─── Dialog de checkout (pagamento simulado) ──────────────────────────────────

function CheckoutDialog({
  plan,
  billing,
  onClose,
  onSuccess,
}: {
  plan: Plan;
  billing: BillingSummary | undefined;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const isUpgrade =
    billing?.plan && Number(plan.basePriceBrl) > billing.plan.basePriceBrl;
  const isDowngrade =
    billing?.plan && Number(plan.basePriceBrl) < billing.plan.basePriceBrl;

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await subscriptionsTenantService.checkout({ planId: plan.id });
      toast.success(`Plano ${plan.label} ativado com sucesso!`);
      onSuccess();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Não foi possível processar o checkout. Tente novamente.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !loading && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-primary" />
            {billing?.plan
              ? isUpgrade
                ? 'Fazer upgrade de plano'
                : isDowngrade
                  ? 'Fazer downgrade de plano'
                  : 'Trocar de plano'
              : 'Contratar plano'}
          </DialogTitle>
          <DialogDescription>
            Confirme os detalhes abaixo. A cobrança é simulada (sem gateway de pagamento real).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="rounded-lg border bg-muted/40 p-4 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Plano</span>
              <span className="font-medium">{plan.label}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Armazenamento</span>
              <span className="font-medium">{formatStorage(plan.storageGbIncluded)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Usuários incluídos</span>
              <span className="font-medium">{plan.includedUsers}</span>
            </div>
            <Separator />
            <div className="flex justify-between text-base font-semibold">
              <span>Valor mensal</span>
              <span>R$ {formatBrl(Number(plan.basePriceBrl))}</span>
            </div>
          </div>

          <p className="text-xs text-muted-foreground">
            A alteração entra em vigor imediatamente. Caso seu número de usuários atuais seja
            maior que o incluído neste plano, o valor por usuário extra será aplicado normalmente.
          </p>
        </div>

        <DialogFooter className="flex flex-wrap gap-2 justify-end">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button onClick={handleConfirm} disabled={loading} className="gap-2">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            Confirmar e ativar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
